import React, { FC, useMemo, useRef } from 'react';
import { useHistory } from 'react-router';

import DataGrid, {
  Column,
  Editing,
  FilterRow,
  Form,
  Pager,
  Paging,
  Popup,
  RequiredRule,
} from 'devextreme-react/data-grid';
import { Item } from 'devextreme-react/form';
import 'devextreme-react/text-area';

import { AppCard, AppLayoutTop } from 'components/appComponents';
import { useAuth } from 'context/auth';
import { Button } from 'devextreme-react';
import CustomStore from 'devextreme/data/custom_store';
import { requestHelper } from 'utils/request-helper';

import AddModal from './modals/add';

type NomenclaturePageProps = {
  onLoaded: (e: any) => void;
  changeId: (id: number) => void;
};

const NomenclaturePage: FC<NomenclaturePageProps> = ({ onLoaded, changeId }) => {
  const history = useHistory();
  const { getClaims }: any = useAuth();

  const NomenclatureDataStore: any = useMemo(
    () =>
      new CustomStore({
        key: 'id',
        load: (loadOptions: any) => requestHelper('/v1/Nomenclature', 'get', loadOptions),
        insert: async (values) => {
          await requestHelper('/v1/Nomenclature', 'post', {
            values: values,
          });
        },

        remove: async (key) =>
          await requestHelper(`/v1/Nomenclature/${key}`, 'delete', {
            key: key,
          }),

        update: async (key, values) =>
          await requestHelper(`/v1/Nomenclature/${key}`, 'put', {
            key: key,
            values: values,
          }),
        onLoaded: onLoaded,
      }),
    [onLoaded],
  );

  const refGrid = useRef<DataGrid | null>(null);
  const refresh = () => refGrid.current?.instance.refresh();

  const onSelectionChanged = (e: any) => {
    const id = e.selectedRowKeys[0];
    changeId(id);
  };

  const handleClickHistory = (id: number) => {
    history.push(`/nomenclature-history/${id}`);
  };

  const isCanShowHistory = getClaims(['CanLookNomenclatureHistory']);
  const isCanEditNomenclature = getClaims(['CanEditNomenclature']);

  return (
    <div>
      <AppLayoutTop title="Рецепты">{isCanEditNomenclature && <AddModal refresh={refresh} />}</AppLayoutTop>

      <AppCard>
        <DataGrid
          dataSource={NomenclatureDataStore}
          showBorders={false}
          loadPanel={{
            enabled: true,
            text: 'Загрузка',
          }}
          ref={refGrid}
          remoteOperations={true}
          onRowUpdating={(e) => (e.newData = { ...e.oldData, ...e.newData })}
          onInitNewRow={(e) =>
            (e.data = {
              name: '',
              printName: '',
              nominalComposition: '',
              strengthClass: '',
              requiredStrength: '',
              strengthInMiddleAge: '',
            })
          }
          selection={{ mode: 'single' }}
          onSelectionChanged={onSelectionChanged}
        >
          <FilterRow visible={true} />
          <Editing
            mode="popup"
            allowUpdating={isCanEditNomenclature}
            texts={{
              editRow: 'Просмотр',
            }}
            allowDeleting={isCanEditNomenclature}
          >
            <Popup title="Просмотр информации" showTitle={true} width={500} height="auto" />
            <Form>
              <Item itemType="group" colCount={2} colSpan={2}>
                <Item dataField="comment" caption="Наименование" colSpan={2}>
                  <RequiredRule message="Заполните поле Наименование" />
                </Item>
                <Item dataField="name" caption="Марка" colSpan={2}>
                  <RequiredRule message="Заполните поле Марка" />
                </Item>
                <Item dataField="printName" caption="Марка на печать" colSpan={2}>
                  <RequiredRule message="Заполните поле Имя на печать" />
                </Item>
                <Item dataField="nominalComposition" colSpan={2} editorOptions={{ maxLength: 10 }} />
                <Item dataField="strengthClass" colSpan={2} editorOptions={{ maxLength: 10 }} />
                <Item dataField="requiredStrength" colSpan={2} editorOptions={{ maxLength: 10 }} />
                <Item dataField="strengthInMiddleAge" colSpan={2} editorOptions={{ maxLength: 10 }} />
              </Item>
            </Form>
          </Editing>

          <Column dataField="comment" caption="Наименование" />
          <Column dataField="name" caption="Марка" />
          <Column dataField="printName" caption="Марка на печать" width={200} />
          <Column dataField="nominalComposition" caption="Номер номинального состава бетонной смеси" visible={false} />
          <Column dataField="strengthClass" caption="Класс по прочности" visible={false} />
          <Column dataField="requiredStrength" caption="Требуемая прочность" visible={false} />
          <Column dataField="strengthInMiddleAge" caption="Прочность в промежуточном возрасте" visible={false} />
          <Column
            dataField=" "
            width={40}
            visible={isCanShowHistory}
            cellRender={({ data }) => (
              <div>
                <Button icon="folder" type="default" stylingMode="text" onClick={() => handleClickHistory(data.id)} />
              </div>
            )}
          />
          <Paging defaultPageSize={30} />
          <Pager showPageSizeSelector={true} allowedPageSizes={[15, 30, 50, 100]} />
        </DataGrid>
      </AppCard>
    </div>
  );
};

export default NomenclaturePage;
